import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, Users, MessageSquare, CalendarClock, Image as ImageIcon, Trash2, Plus, Settings, Layers, Save, KeyRound, FileText, Send, Check, X, Download, Calendar, Clock, MapPin, Bell } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useContent } from "@/context/ContentContext";
import { api, formatApiError, mediaUrl } from "@/lib/api";
import { GALLERY_CATEGORIES, CONTENT_FIELDS, STAFF_ROLE_OPTIONS } from "@/data/content";
import { ImageUploader } from "@/components/ImageUploader";
import { MediaManager } from "@/components/MediaManager";
import { PortfolioAdmin, WEDDINGS_ADMIN_CFG, EVENTS_ADMIN_CFG } from "@/components/PortfolioAdmin";
import { FaqAdmin } from "@/components/FaqAdmin";
import { AboutAdmin } from "@/components/AboutAdmin";

const TABS = [
  { id: "overview", label: "Overview", icon: CalendarClock },
  { id: "enquiries", label: "Enquiries", icon: MessageSquare },
  { id: "bookings", label: "Consultations", icon: CalendarClock },
  { id: "gallery", label: "Gallery", icon: ImageIcon },
  { id: "weddings", label: "Weddings", icon: Calendar },
  { id: "events", label: "Events", icon: Calendar },
  { id: "media", label: "Media", icon: ImageIcon },
  { id: "shifts", label: "Shifts", icon: CalendarClock },
  { id: "documents", label: "Documents", icon: FileText },
  { id: "messages", label: "Messages", icon: MessageSquare },
  { id: "website", label: "Website", icon: Settings },
  { id: "about", label: "About Us", icon: FileText },
  { id: "faqs", label: "FAQs", icon: MessageSquare },
  { id: "services", label: "Services", icon: Layers },
  { id: "account", label: "Account", icon: KeyRound },
  { id: "staff", label: "Staff", icon: Users },
];

export default function Dashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("overview");
  const isAdmin = user?.role === "super_admin";
  const canManage = isAdmin || user?.role === "event_manager";
  useEffect(() => { if (!canManage) setTab("shifts"); }, [canManage]);
  const [stats, setStats] = useState(null);
  const [enquiries, setEnquiries] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [gallery, setGallery] = useState([]);
  const [staff, setStaff] = useState([]);
  const [msg, setMsg] = useState("");
  const [shifts, setShifts] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [messages, setMessages] = useState([]);
  const [notifs, setNotifs] = useState([]);
  const [unread, setUnread] = useState(0);
  const [showNotif, setShowNotif] = useState(false);
  const loadNotifCount = () => { api.get("/notifications/unread_count").then((r) => setUnread(r.data.count)).catch(() => {}); };
  const openNotifs = async () => {
    try {
      const { data } = await api.get("/notifications");
      setNotifs(data); setShowNotif(true);
      await api.post("/notifications/read_all"); setUnread(0);
    } catch { /* ignore */ }
  };
  useEffect(() => { loadNotifCount(); const t = setInterval(loadNotifCount, 15000); return () => clearInterval(t); }, []);

  const { content, services, refresh } = useContent();
  const [cform, setCform] = useState({});
  const [svc, setSvc] = useState([]);
  useEffect(() => { setCform(content); }, [content]);
  useEffect(() => { setSvc(services); }, [services]);
  const setSvcField = (id, k, v) => setSvc((prev) => prev.map((x) => (x.id === id ? { ...x, [k]: v } : x)));
  const saveContent = async () => {
    try { await api.put("/content", cform); refresh(); toast.success("Website content updated"); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const saveService = async (s) => {
    try { await api.put(`/services/${s.id}`, { title: s.title, desc: s.desc, image: s.image }); refresh(); toast.success("Service updated"); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const [pwForm, setPwForm] = useState({ current: "", new: "", confirm: "" });
  const changePassword = async (e) => {
    e.preventDefault();
    if (pwForm.new !== pwForm.confirm) { toast.error("New passwords do not match"); return; }
    try {
      await api.post("/auth/change-password", { current_password: pwForm.current, new_password: pwForm.new });
      setPwForm({ current: "", new: "", confirm: "" });
      toast.success("Password updated");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const [shiftForm, setShiftForm] = useState({ description: "", date: "", time: "", location: "", assignee_ids: [] });
  const createShift = async (e) => {
    e.preventDefault();
    try { await api.post("/shifts", shiftForm); setShiftForm({ description: "", date: "", time: "", location: "", assignee_ids: [] }); load(); toast.success("Shift sent"); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const respondShift = async (id, status) => {
    try { await api.post(`/shifts/${id}/respond`, { status }); load(); toast.success(`Shift ${status}`); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const [docTitle, setDocTitle] = useState("");
  const [docBusy, setDocBusy] = useState(false);
  const uploadDoc = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !docTitle) { toast.error("Enter a title first"); return; }
    setDocBusy(true);
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await api.post("/documents", { title: docTitle, url: data.url });
      setDocTitle(""); load(); toast.success("Document uploaded");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setDocBusy(false); }
  };
  const deleteDoc = async (id) => { await api.delete(`/documents/${id}`); load(); };
  const [msgForm, setMsgForm] = useState({ subject: "", body: "", recipient_ids: [] });
  const sendMessage = async (e) => {
    e.preventDefault();
    try { await api.post("/messages", msgForm); setMsgForm({ subject: "", body: "", recipient_ids: [] }); load(); toast.success("Message sent"); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const toggleId = (arr, id) => (arr.includes(id) ? arr.filter((x) => x !== id) : [...arr, id]);
  const purgeAll = async () => {
    if (!window.confirm("This permanently deletes ALL enquiries, consultations, shifts, messages, notifications and staff accounts. Gallery, services, site content and your admin account are kept. Continue?")) return;
    try { await api.post("/admin/reset-demo-data", { confirm: "DELETE" }); load(); loadNotifCount(); toast.success("All data cleared"); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const [slotCfg, setSlotCfg] = useState(null);
  const [newTime, setNewTime] = useState("");
  const [slotBusy, setSlotBusy] = useState(false);
  const addSlotTime = () => {
    if (!/^\d{1,2}:\d{2}$/.test(newTime)) { toast.error("Enter a time as HH:MM"); return; }
    setSlotCfg((c) => ({ ...c, times: [...new Set([...(c.times || []), newTime])].sort() }));
    setNewTime("");
  };
  const removeSlotTime = (t) => setSlotCfg((c) => ({ ...c, times: (c.times || []).filter((x) => x !== t) }));
  const saveSlotCfg = async () => {
    setSlotBusy(true);
    try {
      const { data } = await api.put("/admin/consultation-config", {
        times: slotCfg.times,
        days_ahead: Number(slotCfg.days_ahead) || 21,
        skip_sundays: !!slotCfg.skip_sundays,
      });
      setSlotCfg(data);
      load();
      toast.success(`Times saved — ${data.created} slots created`);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSlotBusy(false); }
  };

  const load = () => {
    api.get("/gallery").then((r) => setGallery(r.data)).catch(() => {});
    api.get("/documents").then((r) => setDocuments(r.data)).catch(() => {});
    if (canManage) {
      api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
      api.get("/enquiries").then((r) => setEnquiries(r.data)).catch(() => {});
      api.get("/bookings").then((r) => setBookings(r.data)).catch(() => {});
      if (isAdmin) api.get("/admin/consultation-config").then((r) => setSlotCfg(r.data)).catch(() => {});
      api.get("/staff").then((r) => setStaff(r.data)).catch(() => {});
      api.get("/shifts").then((r) => setShifts(r.data)).catch(() => {});
      api.get("/messages").then((r) => setMessages(r.data)).catch(() => {});
    } else {
      api.get("/shifts/mine").then((r) => setShifts(r.data)).catch(() => {});
      api.get("/messages/mine").then((r) => setMessages(r.data)).catch(() => {});
    }
  };
  useEffect(load, [canManage]);

  const doLogout = () => { logout(); navigate("/staff-login"); };

  // Gallery add
  const [gform, setGform] = useState({ title: "", category: "Weddings", url: "", media_type: "image", span: "normal" });
  const addGallery = async (e) => {
    e.preventDefault(); setMsg("");
    try {
      await api.post("/gallery", gform);
      setGform({ title: "", category: "Weddings", url: "", media_type: "image", span: "normal" });
      load();
    } catch (err) { setMsg(formatApiError(err.response?.data?.detail)); }
  };
  const delGallery = async (id) => { await api.delete(`/gallery/${id}`); load(); };
  const setGalField = (id, k, v) => setGallery((prev) => prev.map((g) => (g.id === id ? { ...g, [k]: v } : g)));
  const saveGallery = async (g) => {
    try { await api.put(`/gallery/${g.id}`, { title: g.title, category: g.category, url: g.url, span: g.span, media_type: g.media_type }); toast.success("Gallery item updated"); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  // Staff add
  const [sform, setSform] = useState({ name: "", email: "", password: "", role: "staff" });
  const addStaff = async (e) => {
    e.preventDefault(); setMsg("");
    try {
      await api.post("/staff", sform);
      setSform({ name: "", email: "", password: "", role: "staff" });
      load();
    } catch (err) { setMsg(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <div data-testid="dashboard-page" className="min-h-screen bg-[#F1EFE8] flex">
      {/* Sidebar */}
      <aside className="w-16 md:w-64 bg-[#0A0A0A] text-white flex flex-col fixed h-full">
        <div className="p-4 md:p-6 border-b border-white/10">
          <img src="/logo.png" alt="DonoDidio" className="hidden md:block h-12 w-auto" />
          <img src="/logo.png" alt="DD" className="md:hidden h-8 w-auto" />
        </div>
        <nav className="flex-1 py-6 space-y-1">
          {TABS.filter((t) => {
            if (t.id === "staff") return isAdmin;
            if (t.id === "media") return isAdmin;
            if (["overview", "enquiries", "bookings", "website", "services", "gallery", "weddings", "events", "faqs", "about"].includes(t.id)) return canManage;
            return true;
          }).map((t) => {
            const Icon = t.icon;
            return (
              <button
                key={t.id}
                data-testid={`tab-${t.id}`}
                onClick={() => setTab(t.id)}
                className={`w-full flex items-center gap-3 px-4 md:px-6 py-3 text-sm transition-colors ${
                  tab === t.id ? "bg-[#D4AF37] text-black" : "text-white/70 hover:text-white hover:bg-white/5"
                }`}
              >
                <Icon size={18} /> <span className="hidden md:inline">{t.label}</span>
              </button>
            );
          })}
        </nav>
        <button onClick={doLogout} data-testid="logout-btn" className="flex items-center gap-3 px-4 md:px-6 py-4 text-white/70 hover:text-[#D4AF37] border-t border-white/10">
          <LogOut size={18} /> <span className="hidden md:inline">Sign Out</span>
        </button>
      </aside>

      <main className="flex-1 ml-16 md:ml-64 p-6 md:p-12">
        <div className="flex items-center justify-between mb-10">
          <div>
            <p className="overline mb-2">{user?.role?.replace(/_/g, " ")}</p>
            <h1 className="font-serif-lux text-3xl md:text-4xl font-light">Welcome, {user?.name?.split(" ")[0]}</h1>
          </div>
        </div>

        {msg && <div className="mb-6 border border-red-300 bg-red-50 text-red-700 px-4 py-3 text-sm">{msg}</div>}

        {tab === "overview" && stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-5" data-testid="overview-stats">
            {[["Enquiries", stats.enquiries, "enquiries"], ["Consultations", stats.bookings, "bookings"], ["Gallery Items", stats.gallery, "gallery"], ["Team Members", stats.staff, "staff"]].map(([l, v, target]) => (
              <button key={l} onClick={() => setTab(target)} data-testid={`stat-${target}`} className="text-left bg-white p-7 border border-[#E5E5DF] hover:border-[#D4AF37] hover:shadow-lg transition-all cursor-pointer">
                <div className="font-serif-lux text-5xl font-light text-[#0A0A0A]">{v}</div>
                <div className="text-xs uppercase tracking-[0.14em] text-[#8a8a80] mt-2">{l}</div>
              </button>
            ))}
          </div>
        )}

        {tab === "enquiries" && (
          <div className="bg-white border border-[#E5E5DF] overflow-x-auto" data-testid="enquiries-table">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-[#8a8a80] uppercase text-xs tracking-wider border-b border-[#E5E5DF]">
                <th className="p-4">Name</th><th className="p-4">Event</th><th className="p-4">Contact</th><th className="p-4">Date</th><th className="p-4">Vision</th>
              </tr></thead>
              <tbody>
                {enquiries.map((e) => (
                  <tr key={e.id} className="border-b border-[#F1EFE8]">
                    <td className="p-4 font-medium">{e.name}</td>
                    <td className="p-4">{e.event_type}</td>
                    <td className="p-4 text-[#4A4A4A]">{e.email}<br />{e.phone}</td>
                    <td className="p-4">{e.event_date || "—"}</td>
                    <td className="p-4 text-[#4A4A4A] max-w-xs truncate">{e.description || "—"}</td>
                  </tr>
                ))}
                {enquiries.length === 0 && <tr><td colSpan={5} className="p-10 text-center text-[#8a8a80]">No enquiries yet.</td></tr>}
              </tbody>
            </table>
          </div>
        )}

        {tab === "bookings" && (
          <div className="space-y-6">
            {isAdmin && slotCfg && (
              <div className="bg-white border border-[#E5E5DF] p-6" data-testid="consultation-times-editor">
                <div className="flex items-center gap-2 mb-1">
                  <Clock size={16} className="text-[#B8962E]" />
                  <h3 className="font-serif-lux text-xl font-light">Consultation Times</h3>
                </div>
                <p className="text-xs text-[#8a8a80] mb-5">Set the times offered to clients each day. Shown in UK time (Europe/London). Saving rebuilds upcoming availability — already-booked consultations are never removed.</p>
                <div className="flex flex-wrap gap-2 mb-5">
                  {(slotCfg.times || []).map((t) => (
                    <span key={t} data-testid={`slot-time-${t}`} className="inline-flex items-center gap-2 pl-3 pr-2 py-1.5 bg-[#F1EFE8] text-sm rounded-full">
                      {t}
                      <button onClick={() => removeSlotTime(t)} data-testid={`remove-time-${t}`} aria-label={`Remove ${t}`} className="text-[#8a8a80] hover:text-red-600"><X size={14} /></button>
                    </span>
                  ))}
                  {(slotCfg.times || []).length === 0 && <span className="text-sm text-[#8a8a80]">No times set yet.</span>}
                </div>
                <div className="flex flex-wrap items-end gap-4">
                  <div>
                    <label className="label-lux block mb-1">Add time (UK)</label>
                    <input type="time" className="input-lux" value={newTime} onChange={(e) => setNewTime(e.target.value)} data-testid="new-slot-time" />
                  </div>
                  <button onClick={addSlotTime} className="btn-outline-dark !py-2 !px-4 inline-flex items-center" data-testid="add-slot-time"><Plus size={14} className="mr-1" /> Add</button>
                  <div>
                    <label className="label-lux block mb-1">Days ahead</label>
                    <input type="number" min="1" max="90" className="input-lux w-24" value={slotCfg.days_ahead} onChange={(e) => setSlotCfg((c) => ({ ...c, days_ahead: e.target.value }))} data-testid="days-ahead" />
                  </div>
                  <label className="flex items-center gap-2 text-sm text-[#4A4A4A] pb-2">
                    <input type="checkbox" checked={!!slotCfg.skip_sundays} onChange={(e) => setSlotCfg((c) => ({ ...c, skip_sundays: e.target.checked }))} data-testid="skip-sundays" />
                    Skip Sundays
                  </label>
                </div>
                <button onClick={saveSlotCfg} disabled={slotBusy} className="btn-gold !py-2 !px-6 mt-5 text-sm inline-flex items-center" data-testid="save-slot-config">
                  <Save size={14} className="mr-1.5" /> {slotBusy ? "Saving…" : "Save & Update Availability"}
                </button>
              </div>
            )}

            <div className="bg-white border border-[#E5E5DF] overflow-x-auto" data-testid="bookings-table">
              <table className="w-full text-sm">
                <thead><tr className="text-left text-[#8a8a80] uppercase text-xs tracking-wider border-b border-[#E5E5DF]">
                  <th className="p-4">Client</th><th className="p-4">When</th><th className="p-4">Email</th><th className="p-4">Zoom</th>
                </tr></thead>
                <tbody>
                  {bookings.map((b) => (
                    <tr key={b.id} className="border-b border-[#F1EFE8]">
                      <td className="p-4 font-medium">{b.name}</td>
                      <td className="p-4">{new Date(b.starts_at).toLocaleString("en-GB", { timeZone: "Europe/London" })}</td>
                      <td className="p-4 text-[#4A4A4A]">{b.email}</td>
                      <td className="p-4">{b.zoom_join_url ? <a href={b.zoom_join_url} target="_blank" rel="noreferrer" className="text-[#B8962E] underline">Join</a> : <span className="text-[#8a8a80]">Pending</span>}</td>
                    </tr>
                  ))}
                  {bookings.length === 0 && <tr><td colSpan={4} className="p-10 text-center text-[#8a8a80]">No consultations booked yet.</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {tab === "media" && isAdmin && (
          <MediaManager content={content} refresh={refresh} />
        )}

        {tab === "weddings" && canManage && (
          <PortfolioAdmin cfg={WEDDINGS_ADMIN_CFG} content={content} refresh={refresh} />
        )}

        {tab === "events" && canManage && (
          <PortfolioAdmin cfg={EVENTS_ADMIN_CFG} content={content} refresh={refresh} />
        )}

        {tab === "faqs" && canManage && (
          <FaqAdmin content={content} refresh={refresh} />
        )}

        {tab === "about" && canManage && (
          <AboutAdmin content={content} refresh={refresh} />
        )}

        {tab === "gallery" && (
          <div>
            <form onSubmit={addGallery} data-testid="gallery-add-form" className="bg-white border border-[#E5E5DF] p-6 mb-8 space-y-4">
              <div className="grid md:grid-cols-3 gap-4">
                <div><label className="label-lux block mb-1">Title</label><input required className="input-lux" value={gform.title} onChange={(e) => setGform({ ...gform, title: e.target.value })} /></div>
                <div><label className="label-lux block mb-1">Category</label>
                  <select className="input-lux" value={gform.category} onChange={(e) => setGform({ ...gform, category: e.target.value })}>
                    {GALLERY_CATEGORIES.filter((c) => c !== "All").map((c) => <option key={c}>{c}</option>)}
                  </select>
                </div>
                <div><label className="label-lux block mb-1">Type</label>
                  <select className="input-lux" value={gform.media_type} onChange={(e) => setGform({ ...gform, media_type: e.target.value })}>
                    <option value="image">Image</option><option value="video">Video</option>
                  </select>
                </div>
              </div>
              <div><label className="label-lux block mb-1">Media</label>
                <ImageUploader value={gform.url} onChange={(v) => setGform({ ...gform, url: v })} testid="gallery-media" />
              </div>
              <button type="submit" className="btn-gold" data-testid="gallery-add-btn"><Plus size={16} className="mr-1" /> Add to Gallery</button>
            </form>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="gallery-manage-grid">
              {gallery.map((g) => (
                <div key={g.id} className="border border-[#E5E5DF] bg-white p-4 space-y-3" data-testid={`gallery-item-${g.id}`}>
                  <div className="flex gap-3">
                    <img src={mediaUrl(g.url)} alt={g.title} className="w-24 h-20 object-cover flex-shrink-0" />
                    <div className="flex-1 space-y-2">
                      <input className="input-lux !py-1 text-sm" value={g.title} onChange={(e) => setGalField(g.id, "title", e.target.value)} data-testid={`gallery-title-${g.id}`} />
                      <select className="input-lux !py-1 text-sm" value={g.category} onChange={(e) => setGalField(g.id, "category", e.target.value)} data-testid={`gallery-category-${g.id}`}>
                        {GALLERY_CATEGORIES.filter((c) => c !== "All").map((c) => <option key={c}>{c}</option>)}
                      </select>
                    </div>
                  </div>
                  <ImageUploader value={g.url} onChange={(v) => setGalField(g.id, "url", v)} testid={`gallery-img-${g.id}`} />
                  <div className="flex gap-2">
                    <button onClick={() => saveGallery(g)} className="btn-outline-dark !py-2 !px-4 text-xs" data-testid={`gallery-save-${g.id}`}><Save size={14} className="mr-1" /> Save</button>
                    <button onClick={() => delGallery(g.id)} className="!py-2 !px-4 text-xs inline-flex items-center border border-red-400 text-red-500 hover:bg-red-500 hover:text-white transition-colors" data-testid={`gallery-del-${g.id}`}><Trash2 size={14} className="mr-1" /> Remove</button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "website" && canManage && (
          <div data-testid="website-content-editor" className="max-w-3xl space-y-8">
            <div className="bg-white border border-[#E5E5DF] p-6">
              <h3 className="font-serif-lux text-xl mb-5">Website Images</h3>
              <div className="space-y-5">
                {CONTENT_FIELDS.images.map((f) => (
                  <div key={f.key}>
                    <label className="label-lux block mb-2">{f.label}</label>
                    <ImageUploader value={cform[f.key]} onChange={(v) => setCform({ ...cform, [f.key]: v })} testid={`content-${f.key}`} />
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-white border border-[#E5E5DF] p-6">
              <h3 className="font-serif-lux text-xl mb-5">Website Text</h3>
              <div className="space-y-5">
                {CONTENT_FIELDS.texts.map((f) => (
                  <div key={f.key}>
                    <label className="label-lux block mb-2">{f.label}</label>
                    {f.type === "textarea" ? (
                      <textarea rows={2} className="input-lux resize-none" value={cform[f.key] || ""} onChange={(e) => setCform({ ...cform, [f.key]: e.target.value })} data-testid={`content-${f.key}`} />
                    ) : (
                      <input className="input-lux" value={cform[f.key] || ""} onChange={(e) => setCform({ ...cform, [f.key]: e.target.value })} data-testid={`content-${f.key}`} />
                    )}
                  </div>
                ))}
              </div>
            </div>
            <button onClick={saveContent} className="btn-gold" data-testid="save-content-btn"><Save size={16} className="mr-2" /> Save Changes</button>
          </div>
        )}

        {tab === "shifts" && (
          <div data-testid="shifts-panel" className="space-y-8">
            {canManage && (
              <form onSubmit={createShift} data-testid="shift-form" className="bg-white border border-[#E5E5DF] p-6 space-y-4 max-w-3xl">
                <h3 className="font-serif-lux text-xl">Send a Shift</h3>
                <div className="grid md:grid-cols-3 gap-4">
                  <div><label className="label-lux block mb-1">Date</label><input type="date" required className="input-lux" value={shiftForm.date} onChange={(e) => setShiftForm({ ...shiftForm, date: e.target.value })} data-testid="shift-date" /></div>
                  <div><label className="label-lux block mb-1">Time</label><input type="time" required className="input-lux" value={shiftForm.time} onChange={(e) => setShiftForm({ ...shiftForm, time: e.target.value })} data-testid="shift-time" /></div>
                  <div><label className="label-lux block mb-1">Location</label><input required className="input-lux" value={shiftForm.location} onChange={(e) => setShiftForm({ ...shiftForm, location: e.target.value })} data-testid="shift-location" /></div>
                </div>
                <div><label className="label-lux block mb-1">Description</label><textarea rows={2} className="input-lux resize-none" value={shiftForm.description} onChange={(e) => setShiftForm({ ...shiftForm, description: e.target.value })} data-testid="shift-description" /></div>
                <div>
                  <label className="label-lux block mb-2">Assign to (leave empty for all staff)</label>
                  <div className="flex flex-wrap gap-2">
                    {staff.filter((s) => s.role !== "super_admin").map((s) => (
                      <button type="button" key={s.id} onClick={() => setShiftForm({ ...shiftForm, assignee_ids: toggleId(shiftForm.assignee_ids, s.id) })} className={`px-3 py-1.5 text-xs border transition-colors ${shiftForm.assignee_ids.includes(s.id) ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "border-[#1A1A1A]/20 hover:border-[#D4AF37]"}`}>{s.name}</button>
                    ))}
                    {staff.filter((s) => s.role !== "super_admin").length === 0 && <span className="text-xs text-[#8a8a80]">No staff added yet — shift will go to all staff.</span>}
                  </div>
                </div>
                <button type="submit" className="btn-gold" data-testid="shift-send-btn"><Send size={16} className="mr-2" /> Send Shift</button>
              </form>
            )}
            <div className="space-y-4 max-w-3xl">
              {shifts.map((s) => (
                <div key={s.id} className="bg-white border border-[#E5E5DF] p-5" data-testid={`shift-${s.id}`}>
                  <div className="flex flex-wrap gap-4 text-sm mb-2">
                    <span className="flex items-center gap-1"><Calendar size={14} className="text-[#D4AF37]" /> {s.date}</span>
                    <span className="flex items-center gap-1"><Clock size={14} className="text-[#D4AF37]" /> {s.time}</span>
                    <span className="flex items-center gap-1"><MapPin size={14} className="text-[#D4AF37]" /> {s.location}</span>
                  </div>
                  <p className="text-[#4A4A4A] mb-3">{s.description}</p>
                  {canManage ? (
                    <div className="flex flex-wrap gap-2">
                      {s.response_list?.map((r, i) => (
                        <span key={i} className={`text-xs px-2 py-1 ${r.status === "accepted" ? "bg-green-100 text-green-700" : r.status === "declined" ? "bg-red-100 text-red-700" : "bg-[#F1EFE8] text-[#8a8a80]"}`}>{r.name}: {r.status}</span>
                      ))}
                    </div>
                  ) : s.my_status === "pending" ? (
                    <div className="flex gap-3">
                      <button onClick={() => respondShift(s.id, "accepted")} className="btn-gold !py-2 !px-5 text-xs" data-testid={`accept-${s.id}`}><Check size={14} className="mr-1" /> Accept</button>
                      <button onClick={() => respondShift(s.id, "declined")} className="btn-outline-dark !py-2 !px-5 text-xs" data-testid={`decline-${s.id}`}><X size={14} className="mr-1" /> Decline</button>
                    </div>
                  ) : (
                    <span className={`text-xs px-3 py-1.5 ${s.my_status === "accepted" ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}`}>You {s.my_status} this shift</span>
                  )}
                </div>
              ))}
              {shifts.length === 0 && <p className="text-[#8a8a80]">No shifts yet.</p>}
            </div>
          </div>
        )}

        {tab === "documents" && (
          <div data-testid="documents-panel" className="space-y-6 max-w-3xl">
            {canManage && (
              <div className="bg-white border border-[#E5E5DF] p-6 space-y-4">
                <h3 className="font-serif-lux text-xl">Upload Document</h3>
                <input className="input-lux" placeholder="Document title" value={docTitle} onChange={(e) => setDocTitle(e.target.value)} data-testid="doc-title" />
                <label className="btn-gold cursor-pointer inline-flex" data-testid="doc-upload-btn"><Plus size={16} className="mr-1" /> {docBusy ? "Uploading…" : "Choose File & Upload"}<input type="file" className="hidden" onChange={uploadDoc} /></label>
                <p className="text-xs text-[#8a8a80]">Visible to all staff. PDF, Word, Excel, images and more.</p>
              </div>
            )}
            <div className="bg-white border border-[#E5E5DF] divide-y divide-[#F1EFE8]" data-testid="documents-list">
              {documents.map((d) => (
                <div key={d.id} className="flex items-center justify-between px-5 py-4">
                  <div className="flex items-center gap-3"><FileText size={18} className="text-[#D4AF37]" /><span className="font-medium">{d.title}</span></div>
                  <div className="flex items-center gap-4">
                    <a href={mediaUrl(d.url)} target="_blank" rel="noreferrer" className="text-[#B8962E] text-sm inline-flex items-center gap-1" data-testid={`doc-download-${d.id}`}><Download size={14} /> Download</a>
                    {canManage && <button onClick={() => deleteDoc(d.id)} className="text-red-500" data-testid={`doc-del-${d.id}`}><Trash2 size={14} /></button>}
                  </div>
                </div>
              ))}
              {documents.length === 0 && <div className="px-5 py-8 text-center text-[#8a8a80]">No documents yet.</div>}
            </div>
          </div>
        )}

        {tab === "messages" && (
          <div data-testid="messages-panel" className="space-y-6 max-w-3xl">
            {canManage && (
              <form onSubmit={sendMessage} className="bg-white border border-[#E5E5DF] p-6 space-y-4" data-testid="message-form">
                <h3 className="font-serif-lux text-xl">Send Message</h3>
                <input required className="input-lux" placeholder="Subject" value={msgForm.subject} onChange={(e) => setMsgForm({ ...msgForm, subject: e.target.value })} data-testid="msg-subject" />
                <textarea required rows={3} className="input-lux resize-none" placeholder="Write your message…" value={msgForm.body} onChange={(e) => setMsgForm({ ...msgForm, body: e.target.value })} data-testid="msg-body" />
                <div>
                  <label className="label-lux block mb-2">Recipients (leave empty for all staff)</label>
                  <div className="flex flex-wrap gap-2">
                    {staff.filter((s) => s.role !== "super_admin").map((s) => (
                      <button type="button" key={s.id} onClick={() => setMsgForm({ ...msgForm, recipient_ids: toggleId(msgForm.recipient_ids, s.id) })} className={`px-3 py-1.5 text-xs border transition-colors ${msgForm.recipient_ids.includes(s.id) ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "border-[#1A1A1A]/20 hover:border-[#D4AF37]"}`}>{s.name}</button>
                    ))}
                    {staff.filter((s) => s.role !== "super_admin").length === 0 && <span className="text-xs text-[#8a8a80]">No staff added yet — message will go to all staff.</span>}
                  </div>
                </div>
                <button type="submit" className="btn-gold" data-testid="msg-send-btn"><Send size={16} className="mr-2" /> Send Message</button>
              </form>
            )}
            <div className="space-y-3" data-testid="messages-list">
              {messages.map((m) => (
                <div key={m.id} className="bg-white border border-[#E5E5DF] p-5">
                  <div className="flex justify-between items-start gap-4"><h4 className="font-serif-lux text-lg">{m.subject}</h4><span className="text-xs text-[#8a8a80] whitespace-nowrap">{new Date(m.created_at).toLocaleDateString("en-GB")}</span></div>
                  <p className="text-[#4A4A4A] mt-2 whitespace-pre-line">{m.body}</p>
                  {canManage && <p className="text-xs text-[#8a8a80] mt-3">To: {m.recipient_ids?.length ? `${m.recipient_ids.length} staff` : "All staff"}</p>}
                </div>
              ))}
              {messages.length === 0 && <p className="text-[#8a8a80]">No messages yet.</p>}
            </div>
          </div>
        )}

        {tab === "account" && (
          <div className="max-w-md space-y-8">
            <form onSubmit={changePassword} data-testid="account-editor" className="bg-white border border-[#E5E5DF] p-6 space-y-5">
              <h3 className="font-serif-lux text-xl">Change Password</h3>
              <div><label className="label-lux block mb-2">Current Password</label>
                <input type="password" required className="input-lux" value={pwForm.current} onChange={(e) => setPwForm({ ...pwForm, current: e.target.value })} data-testid="pw-current" />
              </div>
              <div><label className="label-lux block mb-2">New Password</label>
                <input type="password" required className="input-lux" value={pwForm.new} onChange={(e) => setPwForm({ ...pwForm, new: e.target.value })} placeholder="At least 8 characters" data-testid="pw-new" />
              </div>
              <div><label className="label-lux block mb-2">Confirm New Password</label>
                <input type="password" required className="input-lux" value={pwForm.confirm} onChange={(e) => setPwForm({ ...pwForm, confirm: e.target.value })} data-testid="pw-confirm" />
              </div>
              <button type="submit" className="btn-gold" data-testid="change-password-btn"><KeyRound size={16} className="mr-2" /> Update Password</button>
            </form>
            {isAdmin && (
              <div className="bg-white border border-red-200 p-6" data-testid="danger-zone">
                <h3 className="font-serif-lux text-xl text-red-600 mb-2">Danger Zone</h3>
                <p className="text-sm text-[#4A4A4A] mb-4">Permanently delete all enquiries, consultations, shifts, messages, notifications and staff accounts. Gallery, services, site content and your admin account are kept.</p>
                <button onClick={purgeAll} className="!py-2 !px-5 text-xs inline-flex items-center border border-red-500 text-red-600 hover:bg-red-600 hover:text-white transition-colors" data-testid="purge-all-btn"><Trash2 size={14} className="mr-1" /> Delete All Data</button>
              </div>
            )}
          </div>
        )}

        {tab === "services" && canManage && (
          <div data-testid="services-editor" className="max-w-3xl space-y-6">
            {svc.map((s) => (
              <div key={s.id} className="bg-white border border-[#E5E5DF] p-6 space-y-4">
                <div className="flex items-center gap-3">
                  <span className="font-serif-lux text-[#D4AF37] text-2xl">{s.num}</span>
                  <input className="input-lux flex-1 font-medium" value={s.title || ""} onChange={(e) => setSvcField(s.id, "title", e.target.value)} data-testid={`svc-title-${s.num}`} />
                </div>
                <textarea rows={2} className="input-lux resize-none" value={s.desc || ""} onChange={(e) => setSvcField(s.id, "desc", e.target.value)} data-testid={`svc-desc-${s.num}`} />
                <div><label className="label-lux block mb-2">Image</label>
                  <ImageUploader value={s.image} onChange={(v) => setSvcField(s.id, "image", v)} testid={`svc-img-${s.num}`} />
                </div>
                <button onClick={() => saveService(s)} className="btn-outline-dark !py-2 !px-5 text-xs" data-testid={`svc-save-${s.num}`}><Save size={14} className="mr-1" /> Save</button>
              </div>
            ))}
          </div>
        )}

        {tab === "staff" && isAdmin && (
          <div>
            <form onSubmit={addStaff} data-testid="staff-add-form" className="bg-white border border-[#E5E5DF] p-6 mb-8 grid md:grid-cols-5 gap-4 items-end">
              <div><label className="label-lux block mb-1">Name</label><input required className="input-lux" value={sform.name} onChange={(e) => setSform({ ...sform, name: e.target.value })} /></div>
              <div><label className="label-lux block mb-1">Email</label><input required type="email" className="input-lux" value={sform.email} onChange={(e) => setSform({ ...sform, email: e.target.value })} /></div>
              <div><label className="label-lux block mb-1">Password</label><input required type="text" className="input-lux" value={sform.password} onChange={(e) => setSform({ ...sform, password: e.target.value })} /></div>
              <div><label className="label-lux block mb-1">Role</label>
                <select className="input-lux" value={sform.role} onChange={(e) => setSform({ ...sform, role: e.target.value })}>
                  {STAFF_ROLE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>
              <button type="submit" className="btn-gold" data-testid="staff-add-btn"><Plus size={16} className="mr-1" /> Add</button>
            </form>
            <div className="bg-white border border-[#E5E5DF]" data-testid="staff-table">
              {staff.map((s) => (
                <div key={s.id} className="flex items-center justify-between px-6 py-4 border-b border-[#F1EFE8]">
                  <div><div className="font-medium">{s.name}</div><div className="text-sm text-[#8a8a80]">{s.email}</div></div>
                  <span className="text-xs uppercase tracking-[0.14em] text-[#B8962E]">{s.role.replace(/_/g, " ")}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
